/**
 * Scripted answers for a material page (price, delivery, quality and the
 * agreement terms that matter for it). They used to sit behind an "Ask" button
 * on the page; Nami now offers them as chips (`agent/page-answers.ts`). The
 * scorecard citations point at the page itself, so they carry no link in Nami.
 */
import type { AskCitation, ScriptedAnswer } from '../../ask/types';
import { getClause } from '../../data/clauses';
import { fmtMoney, fmtNumber, fmtPct } from '../../data/format';
import {
  CORVANE_QUALITY_COSTS,
  getMaterial,
  TCO_BREAKDOWN,
} from '../../data/materials';
import { getSupplier } from '../../data/suppliers';
import type { ContractId, MaterialId } from '../../data/types';
import {
  fmtPricePerUnit,
  fmtUnitPrice,
  priceUnitLabel,
} from './materials-format';
import type { SupplierColumn } from './materials-model';
import { activeColumns } from './materials-model';

function scorecardId(materialId: MaterialId): string {
  return `${materialId}-scorecard`;
}

function cite(contractId: ContractId, section: string): AskCitation {
  const quote = getClause(`${contractId}:${section}`)?.quote;
  return { documentId: contractId, ref: section, quote };
}

function scoreCite(
  materialId: MaterialId,
  ref: string,
  label: string,
): AskCitation {
  return { documentId: scorecardId(materialId), ref, label };
}

function priceAnswer(
  materialId: MaterialId,
  columns: SupplierColumn[],
): ScriptedAnswer {
  const unit = getMaterial(materialId).unit;
  const unitLabel = priceUnitLabel(unit);
  const active = activeColumns(columns);
  if (materialId === 'MAT-THHN') {
    const rows = [...TCO_BREAKDOWN].sort((a, b) => a.tco - b.tco);
    const [lowest, second] = rows;
    const corvaneRow = rows.find((r) => r.supplierId === 'sup-corvane');
    const name = (id: string) =>
      columns.find((c) => c.metrics.supplierId === id)?.supplier.shortName ??
      id;
    return {
      question:
        'Who is cheapest on copper wire once quality costs are included?',
      aliases: [
        'Which copper wire supplier has the lowest total cost?',
        'Who is the cheapest copper wire supplier including quality?',
      ],
      thinking: [
        'Reading the supplier scorecard',
        'Adding returns, re-spooling, customer credits and rush orders to unit price',
      ],
      answer: [
        `**${name(lowest.supplierId)}** has the lowest total cost at ${fmtPricePerUnit(lowest.tco, unit)}, even though ${name('sup-corvane')} has the lowest unit price.`,
        ...rows.map(
          (row) =>
            `- ${name(row.supplierId)}: ${fmtPricePerUnit(row.tco, unit)} (unit price ${fmtMoney(row.unitPrice, { cents: true })})`,
        ),
        `Corvane's ${CORVANE_QUALITY_COSTS.ncrs} quality issues (jacket defects, short reels, mislabeled lots) add ${fmtMoney(corvaneRow?.costOfPoorQuality ?? 0, { cents: true })} per ${unitLabel} in returns, re-spooling, customer credits and cut-to-length downtime, and its ${CORVANE_QUALITY_COSTS.expedites} rush orders add ${fmtMoney(corvaneRow?.expedites ?? 0, { cents: true })}. That puts it ${fmtMoney((corvaneRow?.tco ?? 0) - lowest.tco, { cents: true })} above ${name(lowest.supplierId)}. ${name(second.supplierId)} is next.`,
      ].join('\n\n'),
      citations: [
        scoreCite(materialId, 'tco', `Scorecard: total cost per ${unitLabel}`),
      ],
    };
  }
  const cheapest = [...active].sort(
    (a, b) => a.metrics.landedPrice - b.metrics.landedPrice,
  )[0];
  return {
    question: `Who is cheapest on ${getMaterial(materialId).name.split(',')[0]}?`,
    aliases: [
      'Which supplier has the lowest price?',
      'Who is the cheapest supplier?',
    ],
    thinking: ['Reading the supplier scorecard'],
    answer: [
      `**${cheapest?.supplier.shortName ?? 'No supplier'}** has the lowest delivered price at ${fmtPricePerUnit(cheapest?.metrics.landedPrice ?? 0, unit)}.`,
      ...active.map(
        (c) =>
          `- ${c.supplier.shortName}: ${fmtUnitPrice(c.metrics.unitPrice)} unit price, ${fmtUnitPrice(c.metrics.landedPrice)} delivered`,
      ),
    ].join('\n\n'),
    citations: [scoreCite(materialId, 'price', 'Scorecard: price')],
  };
}

function deliveryAnswer(
  materialId: MaterialId,
  columns: SupplierColumn[],
): ScriptedAnswer {
  const measured = activeColumns(columns).filter(
    (c) => c.metrics.otif !== null,
  );
  const best = [...measured].sort(
    (a, b) => (b.metrics.otif ?? 0) - (a.metrics.otif ?? 0),
  )[0];
  return {
    question: 'Which supplier is always on time?',
    aliases: [
      'Who delivers on time?',
      'Which supplier is best on time and in full?',
    ],
    thinking: ['Reading the supplier scorecard'],
    answer: [
      `**${best?.supplier.shortName ?? 'No supplier'}** is the most reliable at ${fmtPct(best?.metrics.otif ?? 0)} on time and in full, with a ${best?.metrics.leadTimeWeeks ?? 0}-week lead time.`,
      ...measured.map(
        (c) =>
          `- ${c.supplier.shortName}: ${fmtPct(c.metrics.otif ?? 0)} on time and in full, ${c.metrics.leadTimeWeeks} weeks lead time`,
      ),
    ].join('\n\n'),
    citations: [scoreCite(materialId, 'delivery', 'Scorecard: delivery')],
  };
}

function qualityAnswer(
  materialId: MaterialId,
  columns: SupplierColumn[],
): ScriptedAnswer {
  const measured = activeColumns(columns).filter((c) => c.metrics.ppm !== null);
  const worst = [...measured].sort(
    (a, b) => (b.metrics.ppm ?? 0) - (a.metrics.ppm ?? 0),
  )[0];
  return {
    question: 'Which supplier has quality issues?',
    aliases: [
      'Who has the worst quality?',
      'Which supplier has the most quality issues?',
    ],
    thinking: ['Reading the supplier scorecard'],
    answer: [
      `**${worst?.supplier.shortName ?? 'No supplier'}** has the weakest quality at ${fmtNumber(worst?.metrics.ppm ?? 0)} defects per million, with ${worst?.metrics.ncrsTtm ?? 0} quality issues in the last 12 months.`,
      ...measured.map(
        (c) =>
          `- ${c.supplier.shortName}: ${fmtNumber(c.metrics.ppm ?? 0)} defects per million, ${c.metrics.ncrsTtm} quality issues`,
      ),
    ].join('\n\n'),
    citations: [scoreCite(materialId, 'quality', 'Scorecard: quality')],
  };
}

function thhnContractAnswers(): ScriptedAnswer[] {
  return [
    {
      question:
        'What happens if Corvane delivers late or ships out-of-spec reels?',
      aliases: ['What are the late delivery penalties for Corvane?'],
      thinking: [
        'Reading SA-CORV-2024-017 section 6',
        'Reading SA-CORV-2024-017 section 8',
      ],
      answer: [
        '**Late delivery:** Corvane pays liquidated damages of 1.5% of the late line value for each week or part of a week late, capped at 10% of the line.',
        '**Out-of-spec lots:** defects must stay at or below 500 per million, and every reel lot ships with a factory test report. Pillar may reject a nonconforming lot, and Corvane must replace or credit it and reimburse the related costs.',
      ].join('\n\n'),
      citations: [
        cite('SA-CORV-2024-017', '6.5'),
        cite('SA-CORV-2024-017', '8.1'),
        cite('SA-CORV-2024-017', '8.3'),
      ],
    },
    {
      question: 'Can we reduce Corvane volume at the renewal?',
      aliases: [
        'When can we change the allocation or exit the Corvane agreement?',
      ],
      thinking: ['Reading SA-CORV-2024-017 sections 2 and 12'],
      answer: [
        'Yes. The agreement is **non-exclusive with no minimum purchase**, so allocation can move at any time. Any annual volume in the agreement is a planning estimate and does not bind Pillar.',
        'The term ends on **December 31, 2026** with no automatic renewal, so any extension needs a signed amendment. Pillar may also terminate for convenience on 180 days notice.',
      ].join('\n\n'),
      citations: [
        cite('SA-CORV-2024-017', '2.1'),
        cite('SA-CORV-2024-017', '2.3'),
        cite('SA-CORV-2024-017', '12.2'),
      ],
    },
  ];
}

function vfdContractAnswers(): ScriptedAnswer[] {
  const lumen = getSupplier('sup-lumen').shortName;
  return [
    {
      question: `Who pays tariffs on ${lumen} shipments?`,
      aliases: [`Can ${lumen} add a tariff surcharge?`],
      thinking: ['Reading SA-LUMN-2025-002 section 5'],
      answer: [
        `**${lumen} pays them.** Shipments are delivered to Hayward DC with duties and tariffs paid by the supplier, and the agreement says all duties, tariffs and import taxes are borne by the supplier and included in the price.`,
        'A separate tariff surcharge line on an invoice is not permitted under the agreement.',
      ].join('\n\n'),
      citations: [
        cite('SA-LUMN-2025-002', '5.1'),
        cite('SA-LUMN-2025-002', '5.3'),
      ],
    },
  ];
}

function breakerContractAnswers(): ScriptedAnswer[] {
  return [
    {
      question: 'What are our rights after the Vitrex quality problem?',
      aliases: [
        'What does the Vitrex quality agreement say about factory changes?',
      ],
      thinking: ['Reading QA-VITX-2023-004 sections 7 and 9'],
      answer: [
        'Vitrex must give **180 days prior notice and obtain Pillar approval** before changing a manufacturing site or subcontractor, and product must ship **only from factories UL has certified**. Lot VX-24117 came from a factory UL has not certified, which breaches this.',
        'Pillar may reject the affected lots and recover its costs, and Vitrex must respond to corrective action request SCAR-0192 within 10 business days.',
      ].join('\n\n'),
      citations: [
        cite('QA-VITX-2023-004', '7.2'),
        cite('QA-VITX-2023-004', '7.5'),
        cite('QA-VITX-2023-004', '9.1'),
      ],
    },
  ];
}

const CONTRACT_ANSWERS: Partial<Record<MaterialId, () => ScriptedAnswer[]>> = {
  'MAT-THHN': thhnContractAnswers,
  'MAT-VFD': vfdContractAnswers,
  'MAT-MCCB': breakerContractAnswers,
};

/** Scripted answers for a material page, offered as chips in Nami. */
export function buildMaterialSuggestions(
  materialId: MaterialId,
  columns: SupplierColumn[],
): ScriptedAnswer[] {
  return [
    priceAnswer(materialId, columns),
    deliveryAnswer(materialId, columns),
    qualityAnswer(materialId, columns),
    ...(CONTRACT_ANSWERS[materialId]?.() ?? []),
  ];
}
